import { useEffect, useState } from 'react';
import { Loader2, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  useEnqueueToPublishQueueMutation,
  useMyPublishQueuesQuery,
} from '@/generated/graphql';
import { useClipTitles } from '@/hooks/use-clip-titles';
import {
  isApprovedEnqueueErrorCode,
  mapEnqueueError,
} from '@/lib/publishQueues/mapEnqueueError';
import {
  formatLocalTime,
  viewerTimeZone,
} from '@/lib/publishing/calendarMonth';
import { extractGraphqlErrorCode } from '@/lib/publishing/mapPublishError';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

export type SendToQueueDialogProps = {
  open: boolean;
  clipId: string;
  clipTitle: string;
  onOpenChange: (open: boolean) => void;
  onEnqueued?: (scheduledAts: string[]) => void;
};

export function SendToQueueDialog({
  open,
  clipId,
  clipTitle,
  onOpenChange,
  onEnqueued,
}: SendToQueueDialogProps) {
  const { t, i18n } = useTranslation();
  const [queueId, setQueueId] = useState('');
  const [title, setTitle] = useState(clipTitle);
  const [description, setDescription] = useState(clipTitle);
  const [caption, setCaption] = useState(clipTitle);
  const [generationRequested, setGenerationRequested] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const { data, loading: queuesLoading } = useMyPublishQueuesQuery({ skip: !open });
  const [enqueue, { loading: submitting }] = useEnqueueToPublishQueueMutation();
  const {
    titlesData,
    isGenerating,
    isPolling,
    error: generationError,
    generateTitles,
  } = useClipTitles(clipId);

  useEffect(() => {
    if (!open) return;
    setQueueId('');
    setTitle(clipTitle);
    setDescription(clipTitle);
    setCaption(clipTitle);
    setGenerationRequested(false);
    setErrorKey(null);
  }, [clipId, clipTitle, open]);

  useEffect(() => {
    if (!generationRequested || !titlesData) return;
    const generatedTitle = titlesData.title_list?.[0]?.trim();
    const generatedDescription = titlesData.description?.trim();
    if (generatedTitle) setTitle(generatedTitle);
    if (generatedDescription) {
      setDescription(generatedDescription);
      setCaption(generatedDescription);
    }
    setGenerationRequested(false);
  }, [generationRequested, titlesData]);

  const queues = (data?.myPublishQueues ?? []).filter((queue) => queue.active);
  const generating = isGenerating || isPolling;

  const handleGenerate = async () => {
    setGenerationRequested(true);
    setErrorKey(null);
    await generateTitles();
  };

  const handleSubmit = async () => {
    if (!queueId || submitting) return;
    setErrorKey(null);
    try {
      const result = await enqueue({
        variables: {
          queueId,
          clipId,
          copy: { title, description, caption },
        },
      });
      const scheduledAts = (result.data?.enqueueToPublishQueue ?? [])
        .map((publish) => publish.scheduled_at)
        .filter((scheduledAt): scheduledAt is string => typeof scheduledAt === 'string');
      const timeZone = viewerTimeZone();
      const times = [...new Set(scheduledAts)]
        .map((scheduledAt) => formatLocalTime(scheduledAt, timeZone, i18n.language))
        .join(', ');
      toast.success(t('publishQueues.send.success', { times }));
      onEnqueued?.(scheduledAts);
      onOpenChange(false);
    } catch (error) {
      setErrorKey(mapEnqueueError(
        extractGraphqlErrorCode(error, isApprovedEnqueueErrorCode),
      ));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('publishQueues.send.titleDialog')}</DialogTitle>
          <DialogDescription>{t('publishQueues.send.descriptionDialog')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="send-to-queue-id">{t('publishQueues.send.queue')}</Label>
            <select
              id="send-to-queue-id"
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={queueId}
              disabled={queuesLoading}
              onChange={(event) => setQueueId(event.target.value)}
            >
              <option value="">{t('publishQueues.send.queuePlaceholder')}</option>
              {queues.map((queue) => (
                <option key={queue.id} value={queue.id}>{queue.name}</option>
              ))}
            </select>
          </div>

          <div className="flex justify-end">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={generating}
              onClick={() => void handleGenerate()}
            >
              {generating
                ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                : <Sparkles className="mr-2 h-4 w-4" />}
              {t(generating ? 'publishQueues.send.generating' : 'publishQueues.send.generate')}
            </Button>
          </div>

          <div className="space-y-2">
            <Label htmlFor="send-to-queue-title">{t('publishQueues.send.title')}</Label>
            <Input
              id="send-to-queue-title"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="send-to-queue-description">
              {t('publishQueues.send.description')}
            </Label>
            <Textarea
              id="send-to-queue-description"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="send-to-queue-caption">{t('publishQueues.send.caption')}</Label>
            <Textarea
              id="send-to-queue-caption"
              value={caption}
              onChange={(event) => setCaption(event.target.value)}
            />
          </div>

          {generationError ? (
            <p role="alert" className="text-sm text-destructive">{generationError}</p>
          ) : null}
          {errorKey ? (
            <p role="alert" className="text-sm text-destructive">{t(errorKey)}</p>
          ) : null}
          {!queuesLoading && queues.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('publishQueues.send.noQueues')}</p>
          ) : null}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t('publishQueues.send.cancel')}
          </Button>
          <Button
            type="button"
            disabled={!queueId || submitting}
            onClick={() => void handleSubmit()}
          >
            {t(submitting ? 'publishQueues.send.submitting' : 'publishQueues.send.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
